import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";

import notify from "../../Notification/helper";
import MissingProgressHeader from "./MissingProgressHeader";
import BarcodeMissingSelectedModal from "./BarcodeMissingSelectedModal";
import MissingReportsToolbar from "./MissingReportsToolbar";
import MissingStatusChip from "./MissingStatusChip";
import {
  fetchMissingProgress,
  fetchMissingRows,
  fetchTaskRunDetails,
  getMissingCsvUrl,
  getBarcodeResolveGatewayRequestPreview,
  lookupGatewayBarcodeByCode,
  lookupGatewayBeefProductIdByBarcodeCode,
  resolveGatewayRetailerContextForMissingRow,
  resolveBarcodeMissingRecord,
  updateMissingStatus,
} from "../../../services/missingReports";

const COLLECTION = "barcode_missing";

const BarcodeMissingReport = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [status, setStatus] = useState("pending");
  const [retailerFilter, setRetailerFilter] = useState({ id: "", label: "" });
  const [includeMissingUnknownRetailers, setIncludeMissingUnknownRetailers] = useState(false);
  const [sortBy, setSortBy] = useState("created_desc");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [progress, setProgress] = useState({ total: 0, resolved: 0, remaining: 0 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [taskRun, setTaskRun] = useState(null);
  const [loadingTaskRun, setLoadingTaskRun] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [retailerId, setRetailerId] = useState("");
  const [retailerName, setRetailerName] = useState("");
  const [retailerMode, setRetailerMode] = useState("lookup");
  const [suggestedRetailerId, setSuggestedRetailerId] = useState("");
  const [suggestedRetailerName, setSuggestedRetailerName] = useState("");
  const [retailerResolutionSource, setRetailerResolutionSource] = useState("none");
  const [hasRetailerRefId, setHasRetailerRefId] = useState(false);
  const [exactRetailerMatchFound, setExactRetailerMatchFound] = useState(false);
  const [beefProductId, setBeefProductId] = useState("");
  const [barcodeCode, setBarcodeCode] = useState("");
  const [gatewayBarcodeStatus, setGatewayBarcodeStatus] = useState({
    checking: false,
    exists: false,
    id: "",
  });

  const csvUrl = useMemo(() => getMissingCsvUrl("barcode"), []);
  const resolveRequestPreview = useMemo(() => {
    if (!selected) {
      return null;
    }

    return getBarcodeResolveGatewayRequestPreview(selected, {
      ownerId: retailerId,
      retailerName,
      beefProductId,
      code: barcodeCode,
      createRetailerIfMissing: false,
    });
  }, [selected, retailerId, retailerName, beefProductId, barcodeCode]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [rowsRes, progressRes] = await Promise.all([
        fetchMissingRows(COLLECTION, {
          page: page + 1,
          limit: rowsPerPage,
          status,
          retailerId: retailerFilter.id,
          includeMissingUnknownRetailers,
          sortBy,
        }),
        fetchMissingProgress(COLLECTION),
      ]);
      setRows(rowsRes.rows);
      setTotal(rowsRes.total);
      setProgress(progressRes);
      return rowsRes.rows || [];
    } catch (e) {
      setError(e.message || "Failed to load barcode missing data.");
      return [];
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, status, retailerFilter.id, includeMissingUnknownRetailers, sortBy]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    let active = true;
    async function loadModalContext() {
      if (!selected) {
        return;
      }
      setLoadingTaskRun(true);
      setTaskRun(null);
      try {
        const [taskRunRes, guessedBeefProduct, retailerContext] = await Promise.all([
          fetchTaskRunDetails(selected.task_run_id),
          lookupGatewayBeefProductIdByBarcodeCode(selected.code || ""),
          resolveGatewayRetailerContextForMissingRow({
            retailerRefId: selected.retailer_ref_id || "",
            rawRetailerName: selected.retailer || "",
          }),
        ]);
        if (!active) {
          return;
        }
        setTaskRun(taskRunRes);
        setRetailerId(retailerContext?.retailerId || "");
        setSuggestedRetailerId(retailerContext?.retailerId || "");
        setSuggestedRetailerName(retailerContext?.retailerName || "");
        setRetailerName(retailerContext?.retailerName || selected.retailer || "");
        setRetailerResolutionSource(retailerContext?.source || "none");
        setHasRetailerRefId(Boolean(retailerContext?.hasRetailerRefId));
        setExactRetailerMatchFound(Boolean(retailerContext?.exactNameMatchFound));
        setRetailerMode("lookup");
        setBeefProductId(guessedBeefProduct || "");
        setBarcodeCode(selected.code || "");
      } catch (e) {
        if (!active) {
          return;
        }
        setError(e.message || "Failed to load task run details.");
      } finally {
        if (active) {
          setLoadingTaskRun(false);
        }
      }
    }
    loadModalContext();
    return () => {
      active = false;
    };
  }, [selected]);

  useEffect(() => {
    let active = true;

    async function checkGatewayBarcode() {
      if (!barcodeCode?.trim()) {
        setGatewayBarcodeStatus({ checking: false, exists: false, id: "" });
        return;
      }

      setGatewayBarcodeStatus({ checking: true, exists: false, id: "" });

      try {
        const existingBarcode = await lookupGatewayBarcodeByCode(barcodeCode.trim());
        if (!active) {
          return;
        }

        setGatewayBarcodeStatus({
          checking: false,
          exists: Boolean(existingBarcode?.id),
          id: existingBarcode?.id || "",
        });
      } catch (e) {
        if (!active) {
          return;
        }
        setGatewayBarcodeStatus({ checking: false, exists: false, id: "" });
      }
    }

    checkGatewayBarcode();

    return () => {
      active = false;
    };
  }, [selected?.id, barcodeCode]);

  const handleStatusOnlyAction = async (nextStatus) => {
    if (!selected) {
      return;
    }
    setActionLoading(true);
    setError("");
    try {
      await updateMissingStatus(COLLECTION, selected.id, nextStatus);
      notify("success", `Record marked as ${nextStatus}.`);
      setSelected(null);
      await loadData();
    } catch (e) {
      setError(e.message || "Failed to update status.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!selected) {
      return false;
    }
    setActionLoading(true);
    setError("");
    try {
      await resolveBarcodeMissingRecord(selected, {
        ownerId: retailerId,
        retailerName,
        beefProductId,
        code: barcodeCode.trim(),
        createRetailerIfMissing: false,
      });
      await updateMissingStatus(COLLECTION, selected.id, "resolved");
      notify("success", "Barcode inserted into Gateway and record resolved.");
      return true;
    } catch (e) {
      setError(e.message || "Failed to resolve barcode missing record.");
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const moveToNextPending = useCallback(
    async (currentId) => {
      const refreshedRows = await loadData();
      const safeRows = Array.isArray(refreshedRows) ? refreshedRows : [];
      const currentIndex = safeRows.findIndex((row) => row.id === currentId);
      const nextOnCurrentPage = safeRows
        .slice(currentIndex >= 0 ? currentIndex + 1 : 0)
        .find((row) => row.status === "pending");

      if (nextOnCurrentPage) {
        setSelected(nextOnCurrentPage);
        return;
      }

      let nextPageToCheck = page + 2;
      while (true) {
        const pageRes = await fetchMissingRows(COLLECTION, {
          page: nextPageToCheck,
          limit: rowsPerPage,
          status,
          retailerId: retailerFilter.id,
          includeMissingUnknownRetailers,
          sortBy,
        });
        const pageRows = pageRes?.rows || [];
        if (!pageRows.length) {
          break;
        }

        const nextInPage = pageRows.find((row) => row.status === "pending");
        if (nextInPage) {
          setPage(nextPageToCheck - 1);
          setRows(pageRows);
          setTotal(pageRes?.total ?? 0);
          setSelected(nextInPage);
          return;
        }

        const totalCount = Number(pageRes?.total ?? 0);
        const lastPage = Math.max(1, Math.ceil(totalCount / rowsPerPage));
        if (nextPageToCheck >= lastPage) {
          break;
        }
        nextPageToCheck += 1;
      }

      setSelected(null);
    },
    [loadData, page, rowsPerPage, status, retailerFilter.id, includeMissingUnknownRetailers, sortBy]
  );

  const handleResolveAndNext = async () => {
    if (!selected) {
      return;
    }

    const currentId = selected.id;
    const resolved = await handleResolve();
    if (!resolved) {
      return;
    }

    await moveToNextPending(currentId);
  };

  const handleSkip = async () => {
    if (!selected) {
      return;
    }
    await moveToNextPending(selected.id);
  };

  const retailerSelectionInvalid = !retailerId;
  const resolveDisabled = retailerSelectionInvalid || !beefProductId || !barcodeCode.trim();

  return (
    <Box
      sx={{
        width: "100%",
        boxShadow: "0 2px 4px rgba(0,0,0,0.5)",
        position: "relative",
        padding: "16px",
        backgroundColor: "#efeeee",
      }}
    >
      <MissingProgressHeader
        title="Barcode / Missing"
        description="Review missing barcode records and resolve them back into Gateway quickly."
        progress={progress}
      />
      <Alert severity="warning" sx={{ mb: 2 }}>
        This missing report workflow is still under development. Validate all changes before finalizing.
      </Alert>

      <MissingReportsToolbar
        status={status}
        onStatusChange={(nextStatus) => {
          setPage(0);
          setStatus(nextStatus);
        }}
        retailerId={retailerFilter.id}
        retailerLabel={retailerFilter.label}
        onRetailerChange={(nextRetailer) => {
          setPage(0);
          setRetailerFilter(nextRetailer);
        }}
        includeMissingUnknownRetailers={includeMissingUnknownRetailers}
        onIncludeMissingUnknownRetailersChange={(nextChecked) => {
          setPage(0);
          setIncludeMissingUnknownRetailers(nextChecked);
        }}
        sortBy={sortBy}
        onSortChange={(nextSortBy) => {
          setPage(0);
          setSortBy(nextSortBy);
        }}
        csvUrl={csvUrl}
      />

      <Table
        sx={{
          width: "100%",
          border: "1px solid #eaeaea",
          backgroundColor: "#fff",
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Retailer</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Updated / Created</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(rows || []).map((row) => (
            <TableRow
              key={row.id}
              hover
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setSelected(row);
                setError("");
              }}
            >
              <TableCell>
                <MissingStatusChip status={row.status} />
              </TableCell>
              <TableCell>{row.code || "-"}</TableCell>
              <TableCell>{row.product_name || row.normalized_product_name || "-"}</TableCell>
              <TableCell>{row.retailer || "-"}</TableCell>
              <TableCell>
                {row.date_updated || row.data_updated || row.date_created
                  ? new Date(row.date_updated || row.data_updated || row.date_created).toLocaleString()
                  : "-"}
              </TableCell>
            </TableRow>
          ))}
          {!loading && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography variant="body2">No missing barcode records found.</Typography>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
      />

      <BarcodeMissingSelectedModal
        selected={selected}
        taskRun={taskRun}
        actionLoading={actionLoading}
        error={error}
        loadingTaskRun={loadingTaskRun}
        onClose={() => setSelected(null)}
        onResolve={handleResolveAndNext}
        onSkip={handleSkip}
        onReject={() => handleStatusOnlyAction("rejected")}
        resolveDisabled={resolveDisabled}
        skipDisabled={!selected}
        retailerId={retailerId}
        retailerName={retailerName}
        retailerMode={retailerMode}
        onRetailerModeChange={setRetailerMode}
        onRetailerNameChange={setRetailerName}
        onRetailerIdChange={setRetailerId}
        suggestedRetailerId={suggestedRetailerId}
        suggestedRetailerName={suggestedRetailerName}
        retailerResolutionSource={retailerResolutionSource}
        hasRetailerRefId={hasRetailerRefId}
        exactRetailerMatchFound={exactRetailerMatchFound}
        beefProductId={beefProductId}
        onBeefProductChange={setBeefProductId}
        barcodeCode={barcodeCode}
        onBarcodeCodeChange={setBarcodeCode}
        gatewayBarcodeStatus={gatewayBarcodeStatus}
        resolveRequestPreview={resolveRequestPreview}
      />
    </Box>
  );
};

export default BarcodeMissingReport;
